import React, { useCallback, useRef } from "react";
import { classNames } from "Utils/utils";
import FloatingPopover, {
  FLOATING_POPOVER_MODE,
  FLOATING_POPOVER_PLACEMENT,
  FLOATING_POPOVER_VARIANT,
} from "components/common/FloatingPopover";
import { FloatingPopoverPanel } from "components/common/FloatingPopover";
import {
  VERTEX360_COPILOT_FLOATING_LOTTIE_SIZE_PX,
  VERTEX360_COPILOT_FLOATING_SIZE_PX,
  VERTEX360_COPILOT_FLOATING_Z_INDEX,
} from "../constants/vertex360CopilotFloatingConstants";
import Vertex360CopilotTooltip from "./Vertex360CopilotTooltip";
import Vertex360CopilotIntroPopover from "./Vertex360CopilotIntroPopover";
import Vertex360CopilotFloatingLottie from "./Vertex360CopilotFloatingLottie";

const DragGripIcon = () => (
  <svg
    width="10"
    height="13"
    viewBox="0 0 10 14"
    fill="currentColor"
    aria-hidden="true"
    className="copilot-floating-btn__grip-icon"
  >
    <circle cx="2.5" cy="2.5" r="1.35" />
    <circle cx="7.5" cy="2.5" r="1.35" />
    <circle cx="2.5" cy="7" r="1.35" />
    <circle cx="7.5" cy="7" r="1.35" />
    <circle cx="2.5" cy="11.5" r="1.35" />
    <circle cx="7.5" cy="11.5" r="1.35" />
  </svg>
);

/**
 * @param {{
 *   fabRef?: React.MutableRefObject<HTMLButtonElement | null>,
 *   onKeyboardOpen?: () => void,
 *   isDragging?: boolean,
 *   introOpen?: boolean,
 *   onDismissIntro?: () => void,
 * }} props
 */
const Vertex360CopilotFloatingButton = ({
  fabRef,
  onKeyboardOpen,
  isDragging = false,
  introOpen = false,
  onDismissIntro,
}) => {
  const localFabRef = useRef(null);

  const setFabRef = useCallback(
    (node) => {
      localFabRef.current = node;
      if (fabRef) {
        fabRef.current = node;
      }
    },
    [fabRef]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onKeyboardOpen?.();
      }
    },
    [onKeyboardOpen]
  );

  const fab = (
    <div className="copilot-floating-btn-wrap">
      <button
        ref={setFabRef}
        type="button"
        onKeyDown={handleKeyDown}
        className={classNames(
          "copilot-floating-btn group relative inline-flex items-center justify-center",
          "rounded-2xl text-[#293F6A]",
          "focus:outline-none active:scale-95",
          isDragging && "copilot-floating-btn--dragging cursor-grabbing scale-[1.02]",
          !isDragging && "cursor-grab"
        )}
        style={{
          width: VERTEX360_COPILOT_FLOATING_SIZE_PX,
          height: VERTEX360_COPILOT_FLOATING_SIZE_PX,
        }}
        aria-label="Open Vertex360 Copilot. Drag to reposition."
        aria-expanded={false}
      >
        <Vertex360CopilotFloatingLottie size={VERTEX360_COPILOT_FLOATING_LOTTIE_SIZE_PX} playing={!isDragging} />
      </button>

      <span className="copilot-floating-btn__grip-external" aria-hidden="true">
        <DragGripIcon />
      </span>

      <span className="copilot-floating-btn__drag-hint" aria-hidden="true">
        Drag
      </span>
    </div>
  );

  return (
    <>
      <FloatingPopover
        mode={FLOATING_POPOVER_MODE.TOOLTIP}
        variant={FLOATING_POPOVER_VARIANT.BRAND}
        placement={FLOATING_POPOVER_PLACEMENT.LEFT}
        anchorRef={localFabRef}
        maxWidth={300}
        offset={12}
        zIndex={VERTEX360_COPILOT_FLOATING_Z_INDEX + 2}
        disabled={isDragging || introOpen}
        contentClassName="copilot-floating-popover-shell !p-0"
        content={<Vertex360CopilotTooltip />}
      >
        {fab}
      </FloatingPopover>

      <FloatingPopoverPanel
        open={introOpen}
        referenceRef={localFabRef}
        variant={FLOATING_POPOVER_VARIANT.BRAND}
        placement={FLOATING_POPOVER_PLACEMENT.LEFT}
        maxWidth={312}
        offsetPx={12}
        zIndex={VERTEX360_COPILOT_FLOATING_Z_INDEX + 3}
        interactive
        showArrow
        contentClassName="copilot-floating-popover-shell !p-0"
      >
        <Vertex360CopilotIntroPopover onDismiss={onDismissIntro} />
      </FloatingPopoverPanel>
    </>
  );
};

export default Vertex360CopilotFloatingButton;
